/**
 * Card Formatting Utilities
 * Provides functions for formatting credit card inputs
 */

/**
 * Format card number with spaces
 */
export const formatCardNumber = (value: string): string => {
  // Remove all non-digits
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '')

  // Get card type for formatting
  const cardType = getCardType(v)

  // Apply formatting based on card type
  switch (cardType) {
    case 'amex':
      return v.replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3').substr(0, 17)
    default:
      return v
        .replace(/(\d{4})/g, '$1 ')
        .trim()
        .substr(0, 19)
  }
}

/**
 * Format card expiry as MM/YYYY
 */
export const formatCardExpiry = (value: string): string => {
  // Remove all non-digits
  const v = value.replace(/\D/g, '')

  if (v.length >= 2) {
    const month = v.substr(0, 2)
    const year = v.substr(2, 4)

    if (year.length > 0) {
      return `${month}/${year}`
    }
    return month
  }

  return v
}

/**
 * Format CVV code
 */
export const formatCardCvc = (value: string, cardBrand: string = ''): string => {
  // Remove all non-digits
  const v = value.replace(/\D/g, '')

  // Amex cards have 4-digit CVV, others have 3
  const maxLength = cardBrand === 'amex' ? 4 : 3

  return v.substr(0, maxLength)
}

/**
 * Get card type from number
 */
const getCardType = (number: string): string => {
  if (number.startsWith('4')) {
    return 'visa'
  }
  if (number.startsWith('5') || number.startsWith('2')) {
    return 'mastercard'
  }
  if (number.startsWith('34') || number.startsWith('37')) {
    return 'amex'
  }
  if (number.startsWith('6')) {
    return 'discover'
  }
  if (number.startsWith('35')) {
    return 'jcb'
  }
  if (number.startsWith('30') || number.startsWith('38')) {
    return 'diners'
  }
  return 'unknown'
}

/**
 * Remove formatting from card number
 */
export const unformatCardNumber = (value: string): string => {
  return value.replace(/\s/g, '')
}

/**
 * Parse expiry date into month and year
 */
export const parseExpiryDate = (expiry: string): { month: string; year: string } => {
  const parts = expiry.split('/')
  return {
    month: parts[0] || '',
    year: parts[1] || '',
  }
}

/**
 * Format expiry for display (MM/YY)
 */
export const formatExpiryDisplay = (month: string, year: string): string => {
  if (!month || !year) {
    return ''
  }
  const shortYear = year.length === 4 ? year.substr(-2) : year
  return `${month.padStart(2, '0')}/${shortYear}`
}
